import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Facebook, Cloud, RefreshCw, Trash2, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const ConnectedAccounts = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});

  useEffect(() => {
    fetchConnectedAccounts();
  }, []);

  const fetchConnectedAccounts = async () => {
    try {
      const response = await axios.get(`${API}/connected-accounts`);
      setAccounts(response.data);
    } catch (error) {
      toast.error('Failed to load connected accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectFacebook = () => {
    const appId = process.env.REACT_APP_FACEBOOK_APP_ID || '1770510201018530';
    const params = new URLSearchParams({
      client_id: appId,
      redirect_uri: `${window.location.origin}/connect/facebook`,
      response_type: 'code',
      scope: 'user_photos,user_posts,public_profile,email'
    });
    
    window.location.href = `https://www.facebook.com/v20.0/dialog/oauth?${params.toString()}`;
  };

  const handleSync = async (accountId) => {
    setSyncing(prev => ({ ...prev, [accountId]: true }));
    try {
      const response = await axios.post(`${API}/connected-accounts/${accountId}/sync`);
      toast.success(response.data.message);
      fetchConnectedAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(prev => ({ ...prev, [accountId]: false }));
    }
  };

  const handleDisconnect = async (accountId) => {
    if (!window.confirm('Disconnect this account? Photos will remain in your library.')) return;
    
    try {
      await axios.delete(`${API}/connected-accounts/${accountId}`);
      toast.success('Account disconnected');
      fetchConnectedAccounts();
    } catch (error) {
      toast.error('Failed to disconnect account');
    }
  };

  const getProviderIcon = (provider) => {
    switch (provider) {
      case 'facebook':
        return <Facebook className="w-6 h-6 text-blue-600" />;
      case 'google_drive':
        return <Cloud className="w-6 h-6 text-green-600" />;
      case 'onedrive':
        return <Cloud className="w-6 h-6 text-blue-500" />;
      default:
        return <Cloud className="w-6 h-6 text-gray-600" />;
    }
  };

  const getSyncStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
            <CheckCircle className="w-3 h-3" />
            Synced
          </span>
        );
      case 'syncing':
        return (
          <span className="flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
            <Loader2 className="w-3 h-3 animate-spin" />
            Syncing...
          </span>
        );
      case 'error':
        return (
          <span className="flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
            <AlertCircle className="w-3 h-3" />
            Error
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
            Pending
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-rose-50 to-blue-50">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-blue-50">
      <header className="bg-white/60 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button data-testid="back-btn" variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Connected Accounts</h1>
              <p className="text-sm text-gray-600">Link your photo sources for automatic import</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Available Connections */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Connect New Account</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <ConnectionCard
              icon={<Facebook className="w-8 h-8 text-blue-600" />}
              title="Facebook"
              description="Import photos and stories"
              onClick={handleConnectFacebook}
              connected={accounts.some(a => a.provider === 'facebook')}
            />
            <ConnectionCard
              icon={<Cloud className="w-8 h-8 text-green-600" />}
              title="Google Drive"
              description="Coming soon"
              onClick={() => toast.info('Google Drive integration coming soon!')}
              disabled
            />
            <ConnectionCard
              icon={<Cloud className="w-8 h-8 text-blue-500" />}
              title="OneDrive"
              description="Coming soon"
              onClick={() => toast.info('OneDrive integration coming soon!')}
              disabled
            />
          </div>
        </div>

        {/* Connected Accounts List */}
        <div>
          <h2 className="text-lg font-semibold mb-4">Your Connected Accounts</h2>
          {accounts.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
              <Cloud className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No accounts connected yet</p>
              <p className="text-sm text-gray-500 mt-2">Connect your accounts to start importing photos automatically</p>
            </div>
          ) : (
            <div className="space-y-4">
              {accounts.map(account => (
                <div
                  key={account.id}
                  data-testid={`account-card-${account.id}`}
                  className="bg-white rounded-2xl p-6 border border-gray-200 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center">
                      {getProviderIcon(account.provider)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 capitalize">{account.provider.replace('_', ' ')}</h3>
                      <p className="text-sm text-gray-600">{account.provider_email || 'Connected'}</p>
                      {account.last_sync_at && (
                        <p className="text-xs text-gray-500">
                          Last synced: {new Date(account.last_sync_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getSyncStatusBadge(account.sync_status)}
                    <Button
                      data-testid={`sync-btn-${account.id}`}
                      onClick={() => handleSync(account.id)}
                      disabled={syncing[account.id] || account.sync_status === 'syncing'}
                      variant="outline"
                      size="sm"
                    >
                      <RefreshCw className={`w-4 h-4 ${syncing[account.id] ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button
                      data-testid={`disconnect-btn-${account.id}`}
                      onClick={() => handleDisconnect(account.id)}
                      variant="outline"
                      size="sm"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-2">How it works:</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>1. Connect your photo accounts (Facebook, Google Drive, etc.)</li>
            <li>2. Click "Sync" to automatically import all your photos</li>
            <li>3. Photos are added to your central library with AI analysis</li>
            <li>4. Create albums by filtering across all your photos</li>
            <li>5. Photos stay synced - reconnect anytime to import new photos</li>
          </ul>
        </div>
      </main>
    </div>
  );
};

const ConnectionCard = ({ icon, title, description, onClick, connected, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled || connected}
    className={`bg-white rounded-2xl p-6 border-2 transition-all ${
      disabled
        ? 'border-gray-200 opacity-50 cursor-not-allowed'
        : connected
        ? 'border-green-300 bg-green-50'
        : 'border-gray-200 hover:border-amber-400 hover:shadow-lg'
    }`}
  >
    <div className="flex flex-col items-center text-center gap-3">
      {icon}
      <div>
        <h3 className="font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
      {connected && (
        <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full">
          Connected
        </span>
      )}
    </div>
  </button>
);

export default ConnectedAccounts;
